import { BetGroupType, BetSlipType } from '@bpos';
import { BetGroup } from '@bpos/common/bet-placement';
import { Bet } from '@bpos/v2/bet-placement';

import { PickId } from '../../../core/picks/pick-id';
import { isBetBuilderPickId } from '../../../core/utils';
import { FailedSlipDetailsType, IBetPicks, IFailedSlipBetBuilderDetails, IFailedSlipDetails, IFailedSlipTeaserDetails } from '../models';

export function getFailedSlipDetails(
    bets: Bet[],
    betGroups: BetGroup[],
    slipIndex: number | undefined,
    betslipType: BetSlipType,
    requestPicks: IBetPicks,
): IFailedSlipDetails | undefined {
    if (isTeaserBetResponse(betslipType, betGroups)) {
        return <IFailedSlipTeaserDetails>{
            type: FailedSlipDetailsType.Teaser,
            isTeaserBet: true,
        };
    }

    const betBuilderPickId = getBetBuilderPickIdForSlip(bets, betGroups, slipIndex, betslipType, requestPicks);

    if (betBuilderPickId) {
        return <IFailedSlipBetBuilderDetails>{
            type: FailedSlipDetailsType.BetBuilder,
            betBuilderPickId,
        };
    }

    return undefined;
}

export function isTeaserBetResponse(betSlipType: BetSlipType, betGroups: BetGroup[]): boolean {
    return betSlipType === BetSlipType.Combo && isTeaserBetGroups(betGroups);
}

export function isTeaserBetGroups(betGroups: BetGroup[]): boolean {
    return betGroups.length > 0 && betGroups.every((group) => group.betGroupType === BetGroupType.Teaser);
}

export function getBetBuilderPickIdForSlip(
    bets: Bet[],
    betGroups: BetGroup[],
    slipIndex: number | undefined,
    betslipType: BetSlipType,
    requestPicks: IBetPicks,
): PickId | undefined {
    if (betslipType === BetSlipType.Single) {
        return getBetBuilderPickIdForSingleSlip(bets, betGroups, slipIndex, requestPicks);
    }

    if (betslipType === BetSlipType.Combo) {
        return getBetBuilderPickIdForComboSlip(bets, betGroups, requestPicks);
    }

    return undefined;
}

function getBetBuilderPickIdForSingleSlip(
    bets: Bet[],
    betGroups: BetGroup[],
    slipIndex: number | undefined,
    requestPicks: IBetPicks,
): PickId | undefined {
    if (betGroups.length !== 0 || bets.length !== 1) {
        return;
    }

    if (slipIndex === undefined) {
        return;
    }
    const pickId = requestPicks.picks[slipIndex]?.id ?? undefined;

    if (!pickId || !isBetBuilderPickId(pickId)) {
        return undefined;
    }

    return pickId;
}

function getBetBuilderPickIdForComboSlip(bets: Bet[], betGroups: BetGroup[], requestPicks: IBetPicks): PickId | undefined {
    if (betGroups.length !== 1 || bets.length <= 1) {
        return;
    }

    const betGroup = betGroups[0];

    if (!betGroup) {
        return undefined;
    }

    // Check if there are bets that are not part of the BetBuilder bet =>
    // in that case this bet is not an SGP but a multi bet (SGP+)
    const isMultiBet = bets.some((b) => !betGroup.bets.includes(b.index));

    if (isMultiBet) {
        return undefined;
    }

    const group = requestPicks.groups[betGroup.index];

    if (!group) {
        return undefined;
    }

    return group.id;
}
